import React, {
    createContext,
    FC,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";

export enum NotificationBannerType {
    Information = "INFORMATION",
    Success = "SUCCESS",
    Attention = "ATTENTION",
    Error = "ERROR",
}

export type NotificationBanner = {
    id: string;
    content: React.ReactNode;
    type: NotificationBannerType;
    allowClose: boolean;
    isGlobal: boolean;
};

type ContextModel = {
    notifications: NotificationBanner[];
    handleAddNotification: (
        content: ReactNode,
        type: NotificationBannerType,
        allowClose: boolean,
        isGlobal: boolean,
        id: string,
    ) => string;
    handleRemoveNotification: (id: string) => void;
    handleRemoveLocalNotifications: () => void;
};

const createNotification = (
    content: ReactNode,
    type: NotificationBannerType,
    allowClose: boolean,
    isGlobal: boolean,
    id: string,
): NotificationBanner => ({
    id: id || Math.floor(Math.random() * Date.now()).toString(),
    content,
    type,
    allowClose,
    isGlobal,
});

const NotificationBannersContext = createContext<ContextModel>(
    {} as ContextModel,
);

export const useNotificationBannersContext = () => {
    const context = useContext(NotificationBannersContext);
    if (context === undefined)
        throw new Error(
            "useNotificationBannersContext must be used within a NotificationBannersProvider",
        );
    return context;
};

export const NotificationBannersProvider: FC<PropsWithChildren<{}>> = ({
    children,
}) => {
    const [notifications, setNotifications] = useState<NotificationBanner[]>(
        [],
    );

    const handleAddNotification = useCallback(
        (
            content: React.ReactNode,
            type: NotificationBannerType,
            allowClose: boolean,
            isGlobal: boolean,
            id: string,
        ) => {
            const notification = createNotification(
                content,
                type,
                allowClose,
                isGlobal,
                id,
            );

            setNotifications((prevNotifications) => [
                ...prevNotifications.filter((x) => x.id !== id),
                notification,
            ]);

            return notification.id;
        },
        [],
    );

    const handleRemoveNotification = useCallback((id: string) => {
        setNotifications((prevNotifications) =>
            prevNotifications.filter((m) => m.id !== id),
        );
    }, []);

    const handleRemoveLocalNotifications = useCallback(() => {
        setNotifications((prevNotifications) =>
            prevNotifications.filter((m) => m.isGlobal),
        );
    }, []);

    const contextValues = useMemo(
        (): ContextModel => ({
            notifications,
            handleAddNotification,
            handleRemoveNotification,
            handleRemoveLocalNotifications,
        }),
        [
            notifications,
            handleAddNotification,
            handleRemoveNotification,
            handleRemoveLocalNotifications,
        ],
    );

    return (
        <NotificationBannersContext.Provider value={contextValues}>
            {children}
        </NotificationBannersContext.Provider>
    );
};
